import { get } from 'lodash';
import * as types from '../constants/types';
import { makeAuthorizedRequest } from '../utilities/rest';
import * as globals from '../constants/globals';
import { CAMPGROUND, PERMIT, TICKET_FACILITY, TIMED_ENTRY, VEHICLE_PERMIT } from '../utilities/roles';
import { doneLoading, startLoading } from '../..';
import { fetchUsers } from './fetchUsers';

export const fetchFacilityInfo = (location, spin) => {
  const doLoading = spin !== false;
  return async (dispatch) => {
    if (doLoading) {
      dispatch(startLoading('Fetching Facility Information...'));
    }

    try {
      const url = `${globals.API_URL}/location/${location.location_type}/${location.location_id}/info`;
      const notesUrl = `${globals.API_INVENTORY_URL}/location-notes/location/${location.location_id}`;

      const [response, notes] = await Promise.all([
        makeAuthorizedRequest(url, 'GET', dispatch),
        makeAuthorizedRequest(notesUrl, 'GET', dispatch),
      ]);

      const info = response.location_info || {};

      info.location_notes = notes.location_notes;

      dispatch({
        type: types.FETCH_FACILITY_INFO_SUCCESS,
        info,
      });

      if (info.location_id && info.location_type) {
        dispatch(fetchUsers([{ location_id: info.location_id, location_type: info.location_type }], false));
      }
    } catch (error) {
      dispatch({
        type: types.FETCH_FACILITY_INFO_ERROR,
        location,
        error,
      });
    }

    if (doLoading) {
      dispatch(doneLoading());
    }
  };
};

const updateFacilityNote = (note, methodType, id) => {
  return {
    type: types.UPDATE_FACILITY_NOTE_SUCCESS,
    note,
    methodType,
    id,
  };
};

const addFacilityNoteError = (id, mandatory) => {
  return {
    type: types.ADD_FACILITY_NOTE_ERROR,
    id,
    mandatory,
  };
};

export const clearFacilityNoteErrors = () => {
  return {
    type: types.CLEAR_FACILITY_NOTE_ERRORS,
  };
};

const submitFacilityNoteInventoryService = (dispatch, location, note, methodType) => {
  let url = `${globals.API_INVENTORY_URL}/location-notes`;
  let body = '';

  if (methodType === 'DELETE') {
    url += `/${note.note_id}`;
  } else {
    const payload = Object.assign({}, note, {
      location_id: location.location_id,
      note_id: note.note_id,
    });
    delete payload.submissionId;
    body = JSON.stringify({ location_note: payload });
  }

  return makeAuthorizedRequest(url, methodType, dispatch, body); //returning a promise to avoid multiple awaits.
};

const submitFacilityNote = (location, note, methodType) => {
  const submissionId = note.submissionId;

  return async (dispatch) => {
    try {
      const response = await submitFacilityNoteInventoryService(dispatch, location, note, methodType);

      const update = response.location_note || note;
      dispatch(updateFacilityNote(update, methodType, submissionId));
    } catch (error) {
      dispatch(addFacilityNoteError(submissionId, note.mandatory));
    }
  };
};

export const submitFacilityNotes = (location, update, remove, create) => {
  return async (dispatch) => {
    dispatch(startLoading('Submitting Facility Notes...'));

    try {
      const createRequests = create.map((note) => submitFacilityNote(location, note, 'POST')(dispatch));
      const updateRequests = update.map((note) => submitFacilityNote(location, note, 'PUT')(dispatch));
      const deleteRequests = remove.map((note) => submitFacilityNote(location, note, 'DELETE')(dispatch));

      await Promise.all(createRequests.concat(updateRequests).concat(deleteRequests));
    } catch (error) {
      // Each request handles its own error
    }

    dispatch(doneLoading());
  };
};

const setFacilityPOCSuccess = (primaryUser, secondaryUser, additionalPOC) => {
  return {
    type: types.UPDATE_FACILITY_POC,
    primaryUser,
    secondaryUser,
    additionalPOC,
  };
};

const setFacilityPOCError = (error) => {
  return {
    type: types.SET_FACILITY_POC_ERROR,
    error,
  };
};

export const clearFacilityPOCError = () => {
  return {
    type: types.CLEAR_FACILITY_POC_ERROR,
  };
};

export const submitFacilityPOC = (location, primaryUser, secondaryUser, additionalPOC) => {
  return async (dispatch) => {
    dispatch(startLoading('Submitting POC Details...'));

    try {
      const pocRequest = JSON.stringify({
        location_id: location.location_id,
        location_type: location.location_type,
        primary_user_id: primaryUser.user_id,
        secondary_user_id: secondaryUser.user_id,
        additional_name: additionalPOC.name,
        additional_role: additionalPOC.role,
        additional_email: additionalPOC.email,
        additional_phone: additionalPOC.phone,
      });

      const url = `${globals.API_URL}/location/${location.location_type}/${location.location_id}/poc`;
      await makeAuthorizedRequest(url, 'PUT', dispatch, pocRequest);

      dispatch(setFacilityPOCSuccess(primaryUser, secondaryUser, additionalPOC));
    } catch (error) {
      dispatch(setFacilityPOCError(error));
    }

    dispatch(doneLoading());
  };
};

const setModalFetchStatus = (key, result, success) => {
  return {
    type: types.SET_FACILITY_MODAL_RESULT,
    key,
    result,
    success,
  };
};

export const typedEnvironmentMap = {
  [CAMPGROUND]: {
    api: process.env.API,
    path: 'camps/internal/campgrounds',
    data: {
      additional: 'campground.notices',
      seasons: 'seasons',
    },
  },
  [PERMIT]: {
    api: process.env.API,
    path: 'permits',
    data: {
      additional: 'payload.description.need_to_know_permit',
    },
  },
  [TICKET_FACILITY]: {
    api: process.env.API,
    path: 'ticket/facility',
    data: {
      additional: 'notes',
    },
  },
  [TIMED_ENTRY]: {
    api: process.env.API,
    path: 'timedentrycontent/facility',
    data: {
      additional: 'notes',
    },
  },
  [VEHICLE_PERMIT]: {
    api: process.env.API,
    path: 'vehiclepermits',
    data: {
      additional: 'payload.description.need_to_know_permit',
    },
  },
};

const fetchModalNotes = (location) => {
  return async (dispatch) => {
    try {
      const response = await makeAuthorizedRequest(
        `${globals.API_INVENTORY_URL}/location-notes/location/${location.location_id}`,
        'GET',
        dispatch
      );

      const notes = get(response, 'location_notes', []);

      dispatch(
        setModalFetchStatus(
          'mandatory',
          notes.filter((note) => note.mandatory),
          true
        )
      );
      dispatch(
        setModalFetchStatus(
          'optional',
          notes.filter((note) => !note.mandatory),
          true
        )
      );
    } catch (error) {
      dispatch(setModalFetchStatus('mandatory', [error], false));
      dispatch(setModalFetchStatus('optional', [error], false));
    }
  };
};

const fetchModalInventoryInfo = (location) => {
  return async (dispatch) => {
    try {
      const map = typedEnvironmentMap[location.location_type];

      const url = [map.api, map.path, location.location_id].join('/');
      const response = await makeAuthorizedRequest(url, 'GET', dispatch);

      const notices = get(response, map.data.additional, []);

      dispatch(setModalFetchStatus('additional', notices, true));
    } catch (error) {
      dispatch(setModalFetchStatus('additional', [error], false));
    }
  };
};

const fetchModalSeasonInfo = (location) => {
  return async (dispatch) => {
    try {
      const map = typedEnvironmentMap[location.location_type];

      const url = [map.api, map.path, location.location_id, 'seasons'].join('/');
      const response = await makeAuthorizedRequest(url, 'GET', dispatch);

      const seasons = get(response, map.data.seasons, []);

      dispatch(setModalFetchStatus('seasons', seasons, true));
    } catch (error) {
      dispatch(setModalFetchStatus('seasons', [error], false));
    }
  };
};

const fetchModalBannerInfo = (location) => {
  return async (dispatch) => {
    try {
      const url = `${process.env.API}/communication/external/alert`;
      const query = `location_id=${encodeURIComponent(location.location_id)}&location_type=${encodeURIComponent(
        location.location_type
      )}`;
      const response = await makeAuthorizedRequest(`${url}?${query}`, 'GET', dispatch);

      const alerts = get(response, 'alerts', []);

      dispatch(setModalFetchStatus('external', alerts, true));
    } catch (error) {
      dispatch(setModalFetchStatus('external', [error], false));
    }
  };
};

export const runFacilityModalFetches = (location) => {
  return async (dispatch) => {
    try {
      const calls = [
        fetchFacilityInfo(location)(dispatch),
        fetchModalNotes(location)(dispatch),
        fetchModalInventoryInfo(location)(dispatch),
        fetchModalBannerInfo(location)(dispatch),
      ];

      if (location.location_type === CAMPGROUND) {
        calls.push(fetchModalSeasonInfo(location)(dispatch));
      }

      await Promise.all(calls);
    } catch (error) {
      // Let each method handle it's own shit
    }
  };
};

export const setLocationFlag = (location, flag, value) => {
  return async (dispatch) => {
    try {
      const inventoryURL = `${globals.API_INVENTORY_URL}/location/${location.location_type}/${location.location_id}/flags`;
      const values = {};
      values[flag] = value;
      const body = {
        values,
      };

      const result = await makeAuthorizedRequest(inventoryURL, 'PUT', dispatch, body);
      dispatch({
        type: types.SET_FACILITY_FLAG_SUCCESS,
        payload: result.collects_mobile_fees,
      });
    } catch (error) {
      console.log(error);
      dispatch({
        type: types.SET_FACILITY_FLAG_ERROR,
        error,
      });
    }
  };
};
